import { getSupabase } from './supabase'

export type AccessType = 'full' | 'single' | string

export interface UserAccess {
  access_type: AccessType
  realm: string | null
  access_code: string | null
  product_key: string | null
}

export interface AccessCodeRow {
  code: string
  realm: string | null
  access_type: AccessType
  is_active: boolean
}

/**
 * Normalize an access code: trim + uppercase.
 */
export function normalizeAccessCode(raw: string): string {
  return raw.trim().toUpperCase()
}

/**
 * Validates an access code against the `access_codes` table.
 */
export async function validateAccessCode(
  code: string,
): Promise<AccessCodeRow | null> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('SUPABASE_NOT_CONFIGURED')

  const normalized = normalizeAccessCode(code)
  const { data, error } = await supabase
    .from('access_codes')
    .select('code, realm, access_type, is_active')
    .eq('code', normalized)
    .eq('is_active', true)
    .maybeSingle()

  if (error) {
    console.error('[Portal] access_codes lookup error:', error)
    throw error
  }
  return (data as AccessCodeRow) || null
}

/**
 * Inserts a row into `user_access` linking the new user to their code.
 */
export async function insertUserAccess(params: {
  userId: string
  email: string
  accessCode: string
  realm: string | null
  accessType: AccessType
}): Promise<void> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('SUPABASE_NOT_CONFIGURED')

  const { error } = await supabase.from('user_access').insert({
    user_id: params.userId,
    email: params.email,
    access_code: params.accessCode,
    realm: params.realm,
    access_type: params.accessType,
  })
  if (error) {
    console.error('[Portal] user_access insert error:', error)
    throw error
  }
}

/**
 * Fetches the user_access row for the authenticated user.
 * Query is ONLY by user_id — never by email or access_code.
 *
 * Returns null when there is no row (no access).
 * Throws when the query itself fails.
 */
export async function fetchUserAccess(
  userId: string,
): Promise<UserAccess | null> {
  const supabase = getSupabase()
  if (!supabase) throw new Error('SUPABASE_NOT_CONFIGURED')

  console.log('[Portal] Fetching user_access for USER ID:', userId)

  const { data: accessData, error: accessError } = await supabase
    .from('user_access')
    .select('user_id, email, access_code, realm, access_type, product_key')
    .eq('user_id', userId)
    .maybeSingle()

  console.log('[Portal] ACCESS DATA:', accessData)
  console.log('[Portal] ACCESS ERROR:', accessError)

  if (accessError) {
    console.error('[Portal] user_access lookup error:', accessError)
    throw accessError
  }
  if (!accessData) return null

  return {
    access_type: (accessData.access_type as AccessType) || 'single',
    realm: accessData.realm || null,
    access_code: accessData.access_code || null,
    product_key: (accessData as any).product_key || null,
  }
}

/**
 * Determines whether the user can access a given reino id.
 *
 * Grants FULL access when any of these match — this makes the check
 * resilient to differences in how the row was populated:
 *   - access_type === 'full'
 *   - product_key === 'LOS12-COMPLETO'
 *   - realm === 'todos'   (sentinel meaning "all reinos")
 *
 * Otherwise grants access only when reinoId === access.realm.
 */
export function canAccessReino(
  access: UserAccess | null,
  reinoId: string,
): boolean {
  if (!access) return false
  if (hasFullAccess(access)) return true
  return access.realm === reinoId
}

/**
 * True when the user's access grants visibility to all 12 reinos.
 */
export function hasFullAccess(access: UserAccess | null): boolean {
  if (!access) return false
  if (access.access_type === 'full') return true
  if ((access.product_key || '').toUpperCase() === 'LOS12-COMPLETO') return true
  if ((access.realm || '').toLowerCase() === 'todos') return true
  return false
}
